/**
 * Canonical KPI registry — one row per KPI, shared by scorecard, charts, 予測 and 目標.
 *
 * Mirrored on the backend by `app/services/kpi_registry.py`; the column names here are the
 * contract for both `GET /dashboard/kpis` (実績, from `all_pos_monthly`) and
 * `GET /dashboard/forecasts` (予測, from `ml_service.ml_forecast`).
 *
 * Three key spaces exist for historical reasons and all three must keep working:
 *   `key`       scorecard key (also the registry id)
 *   `focusKey`  key persisted in Firestore goals (differs only for 客数 → customerCount)
 *   `chartKey`  chart metric key used by chartTargets / LineChart
 */

/** 技術売上 = direct column, else 総売上 − 店販売上. */
export function deriveTechSales(k) {
  if (!k) return null;
  if (k.technical_sales != null && !Number.isNaN(Number(k.technical_sales))) {
    return Number(k.technical_sales);
  }
  if (k.total_sales == null || k.retail_sales == null) return null;
  return k.total_sales - k.retail_sales;
}

/** 客数 = direct column, else 新規 + リピート. */
export function deriveCustomerCount(k) {
  if (!k) return null;
  const direct = k.customer_count ?? k.total_customers;
  if (direct != null && !Number.isNaN(Number(direct))) return Number(direct);
  const n = k.new_customer_count ?? k.new_customers;
  const r = k.repeat_customer_count ?? k.repeat_customers;
  if (n == null || r == null) return null;
  const sum = Number(n) + Number(r);
  return Number.isNaN(sum) ? null : sum;
}

/** 客単価 = direct column, else 総売上 / 客数. */
export function deriveUnitPrice(k) {
  if (!k) return null;
  if (k.unit_price != null && !Number.isNaN(Number(k.unit_price))) return Math.round(Number(k.unit_price));
  if (k.total_sales == null) return null;
  const count = deriveCustomerCount(k);
  if (!count) return null;
  return Math.round(k.total_sales / count);
}

/** 技術単価 = direct column, else 技術売上 / 客数. */
export function deriveTechUnitPrice(k) {
  if (!k) return null;
  if (k.technical_unit_price != null && !Number.isNaN(Number(k.technical_unit_price))) {
    return Math.round(Number(k.technical_unit_price));
  }
  const tech = deriveTechSales(k);
  if (tech == null) return null;
  const count = deriveCustomerCount(k);
  if (!count) return null;
  return Math.round(tech / count);
}

/**
 * @typedef {Object} KpiRegistryEntry
 * @property {string} key            scorecard / registry id
 * @property {string} label          Japanese label shown on cards and charts
 * @property {'currency'|'count'|'percent'|'days'|'pieces'} unit
 * @property {'up'|'down'} better    direction that counts as improvement
 * @property {string} icon
 * @property {boolean} scorecard     rendered as a Home card
 * @property {string|null} chartKey  chart metric key (null = no chart)
 * @property {string[]} actualFields API fields for 実績, first non-null wins
 * @property {((row: object) => number|null)|null} actualDerive identity fallback
 * @property {string|null} forecastField ml_forecast column (null = no 予測 available)
 * @property {string|null} scenarioField 目標達成に必要 scenario row field
 * @property {string} focusKey       key stored in goals.focusKey / subFocusKey
 * @property {boolean} targetable    may hold a 注力 target
 * @property {number|null} benchmark soft healthy benchmark for GAP ranking
 * @property {'benchmark'|'retailShare'|null} gapRule how GAP ranking scores this KPI
 *   (null = never recommended automatically; still selectable by the user)
 */

/** @type {Partial<KpiRegistryEntry>[]} */
const KPI_ENTRIES = [
  {
    key: 'totalSales',
    label: '総売上',
    unit: 'currency',
    better: 'up',
    icon: '💴',
    scorecard: true,
    chartKey: 'revenue',
    actualFields: ['total_sales'],
    actualDerive: null,
    forecastField: 'total_sales',
    scenarioField: 'total_sales',
    focusKey: 'totalSales',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'techSales',
    label: '技術売上',
    unit: 'currency',
    better: 'up',
    icon: '✂️',
    scorecard: true,
    chartKey: 'techSales',
    actualFields: ['technical_sales'],
    actualDerive: deriveTechSales,
    forecastField: 'technical_sales',
    scenarioField: 'tech_sales',
    focusKey: 'techSales',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'retailSales',
    label: '店販売上',
    unit: 'currency',
    better: 'up',
    icon: '🛍️',
    scorecard: true,
    chartKey: 'retailSales',
    actualFields: ['retail_sales'],
    actualDerive: null,
    forecastField: 'retail_sales',
    scenarioField: 'retail_sales',
    focusKey: 'retailSales',
    targetable: true,
    benchmark: null,
    gapRule: 'retailShare',
  },
  {
    key: 'newSales',
    label: '新規売上',
    unit: 'currency',
    better: 'up',
    icon: '🌱',
    scorecard: true,
    chartKey: 'newSales',
    actualFields: ['total_sales_new'],
    actualDerive: null,
    forecastField: 'total_sales_new',
    scenarioField: null,
    focusKey: 'newSales',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'repeatSales',
    label: 'リピート売上',
    unit: 'currency',
    better: 'up',
    icon: '🔄',
    scorecard: true,
    chartKey: 'repeatSales',
    actualFields: ['total_sales_repeat'],
    actualDerive: null,
    forecastField: 'total_sales_repeat',
    scenarioField: null,
    focusKey: 'repeatSales',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'nominationSales',
    label: '指名売上',
    unit: 'currency',
    better: 'up',
    icon: '⭐',
    scorecard: true,
    chartKey: 'nominationSales',
    actualFields: ['nominated_customer_total_sales'],
    actualDerive: null,
    forecastField: 'nominated_customer_total_sales',
    scenarioField: null,
    focusKey: 'nominationSales',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'freeSales',
    label: 'フリー売上',
    unit: 'currency',
    better: 'up',
    icon: '🎫',
    scorecard: true,
    chartKey: 'freeSales',
    actualFields: ['free_customer_total_sales'],
    actualDerive: null,
    forecastField: 'free_customer_total_sales',
    scenarioField: null,
    focusKey: 'freeSales',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'customers',
    label: '客数',
    unit: 'count',
    better: 'up',
    icon: '👥',
    scorecard: true,
    chartKey: 'customerCount',
    actualFields: ['customer_count', 'total_customers'],
    actualDerive: deriveCustomerCount,
    forecastField: 'customer_count',
    scenarioField: 'customer_count',
    focusKey: 'customerCount',
    targetable: true,
    benchmark: 40,
    gapRule: 'benchmark',
  },
  {
    key: 'newCustomers',
    label: '新規客数',
    unit: 'count',
    better: 'up',
    icon: '🆕',
    scorecard: true,
    chartKey: 'newCustomerCount',
    actualFields: ['new_customer_count', 'new_customers'],
    actualDerive: null,
    forecastField: 'new_customer_count',
    scenarioField: 'new_customer_count',
    focusKey: 'newCustomers',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'repeatCustomers',
    label: 'リピート客数',
    unit: 'count',
    better: 'up',
    icon: '🔁',
    scorecard: true,
    chartKey: 'repeatCustomerCount',
    actualFields: ['repeat_customer_count', 'repeat_customers'],
    actualDerive: null,
    forecastField: 'repeat_customer_count',
    scenarioField: 'repeat_customer_count',
    focusKey: 'repeatCustomers',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'nominationCustomers',
    label: '指名客数',
    unit: 'count',
    better: 'up',
    icon: '⭐',
    scorecard: true,
    chartKey: 'nominationCustomers',
    actualFields: ['nominated_customers'],
    actualDerive: null,
    forecastField: 'nominated_customers',
    scenarioField: null,
    focusKey: 'nominationCustomers',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'freeCustomers',
    label: 'フリー客数',
    unit: 'count',
    better: 'up',
    icon: '🎟️',
    scorecard: true,
    chartKey: 'freeCustomers',
    actualFields: ['free_customers'],
    actualDerive: null,
    forecastField: 'free_customers',
    scenarioField: null,
    focusKey: 'freeCustomers',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'unitPrice',
    label: '客単価',
    unit: 'currency',
    better: 'up',
    icon: '💰',
    scorecard: true,
    chartKey: 'customerUnitPrice',
    actualFields: ['unit_price'],
    actualDerive: deriveUnitPrice,
    forecastField: 'unit_price',
    scenarioField: 'unit_price',
    focusKey: 'unitPrice',
    targetable: true,
    benchmark: 12000,
  },
  {
    key: 'techUnitPrice',
    label: '技術単価',
    unit: 'currency',
    better: 'up',
    icon: '✂️',
    scorecard: true,
    chartKey: 'techUnitPrice',
    actualFields: ['technical_unit_price'],
    actualDerive: deriveTechUnitPrice,
    forecastField: 'technical_unit_price',
    scenarioField: 'tech_unit_price',
    focusKey: 'techUnitPrice',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'nomination',
    label: '指名率',
    unit: 'percent',
    better: 'up',
    icon: '⭐',
    scorecard: true,
    chartKey: 'nominationRate',
    actualFields: ['nomination_rate'],
    actualDerive: null,
    forecastField: 'nomination_rate',
    scenarioField: 'nomination_rate',
    focusKey: 'nomination',
    targetable: true,
    benchmark: 70,
  },
  {
    key: 'newReturn',
    label: '新規再来率',
    unit: 'percent',
    better: 'up',
    icon: '🔁',
    scorecard: true,
    chartKey: 'newReturn',
    actualFields: ['new_return_rate', 'new_customer_return_rate'],
    actualDerive: null,
    forecastField: 'new_customer_return_rate',
    scenarioField: 'new_return_rate',
    focusKey: 'newReturn',
    targetable: true,
    benchmark: 50,
  },
  {
    key: 'otherReturn',
    label: '新規以外再来率',
    unit: 'percent',
    better: 'up',
    icon: '♻️',
    scorecard: true,
    chartKey: 'otherReturn',
    actualFields: ['existing_customer_return_rate'],
    actualDerive: null,
    forecastField: 'existing_customer_return_rate',
    scenarioField: null,
    focusKey: 'otherReturn',
    targetable: true,
    benchmark: 60,
  },
  {
    key: 'churnRate',
    label: '離脱率',
    unit: 'percent',
    better: 'down',
    icon: '📉',
    scorecard: true,
    chartKey: 'churnRate',
    actualFields: ['churn_rate'],
    actualDerive: null,
    forecastField: 'churn_rate',
    scenarioField: null,
    focusKey: 'churnRate',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'productRate',
    label: '店販購入比率',
    unit: 'percent',
    better: 'up',
    icon: '🧴',
    scorecard: true,
    chartKey: 'productRate',
    actualFields: ['retail_purchase_rate'],
    actualDerive: null,
    // 予測 not modeled: no driver for 店販購入比率 in ml/structural (column exists, always NULL)
    forecastField: 'retail_purchase_rate',
    scenarioField: null,
    focusKey: 'productRate',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'karteCount',
    label: 'カルテ枚数',
    unit: 'pieces',
    better: 'up',
    icon: '🗂️',
    scorecard: true,
    chartKey: 'karteCount',
    actualFields: ['karte_count', 'active_customers'],
    actualDerive: null,
    forecastField: 'active_customers',
    scenarioField: null,
    focusKey: 'karteCount',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'visitFreq',
    label: '来店周期',
    unit: 'days',
    better: 'down',
    icon: '📅',
    scorecard: true,
    chartKey: 'visitFrequency',
    actualFields: ['avg_visit_interval_repeat', 'visit_frequency'],
    actualDerive: null,
    forecastField: 'visit_frequency',
    scenarioField: null,
    focusKey: 'visitFreq',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'ltv',
    label: '年間平均LTV',
    unit: 'currency',
    better: 'up',
    icon: '💎',
    scorecard: true,
    chartKey: 'ltv',
    actualFields: ['avg_annual_sales_per_active_customer'],
    actualDerive: null,
    forecastField: 'avg_annual_sales_per_active_customer',
    scenarioField: null,
    focusKey: 'ltv',
    targetable: true,
    benchmark: null,
  },
  {
    key: 'loyalAnnualSales',
    label: '固定客年間売上',
    unit: 'currency',
    better: 'up',
    icon: '🏆',
    scorecard: true,
    chartKey: 'loyalAnnualSales',
    actualFields: ['fixed_customer_annual_sales', 'total_annual_sales_active_customers'],
    actualDerive: null,
    forecastField: 'fixed_customer_annual_sales',
    scenarioField: null,
    focusKey: 'loyalAnnualSales',
    targetable: true,
    benchmark: null,
  },
  // ---- chart-only (not scorecard cards, kept because 予測 exists since v1) ----
  {
    key: 'newTechUnitPrice',
    label: '技術単価(新規)',
    unit: 'currency',
    better: 'up',
    icon: '✂️',
    scorecard: false,
    chartKey: 'newTechnicalUnitPrice',
    actualFields: ['new_technical_unit_price'],
    actualDerive: null,
    forecastField: 'new_technical_unit_price',
    scenarioField: null,
    focusKey: 'newTechUnitPrice',
    targetable: false,
    benchmark: null,
  },
  {
    key: 'repeatTechUnitPrice',
    label: '技術単価(リピート)',
    unit: 'currency',
    better: 'up',
    icon: '✂️',
    scorecard: false,
    chartKey: 'repeatTechnicalUnitPrice',
    actualFields: ['repeat_technical_unit_price'],
    actualDerive: null,
    forecastField: 'repeat_technical_unit_price',
    scenarioField: null,
    focusKey: 'repeatTechUnitPrice',
    targetable: false,
    benchmark: null,
  },
];

/** @type {KpiRegistryEntry[]} */
export const KPI_REGISTRY = KPI_ENTRIES.map((e) => ({ gapRule: null, ...e }));

/** ml_forecast columns that carry `_lower` / `_upper` bands (the original 8, mode=REQUIRED). */
export const FORECAST_BAND_COLUMNS = [
  'total_sales',
  'nomination_rate',
  'new_customer_count',
  'new_technical_unit_price',
  'repeat_customer_count',
  'visit_frequency',
  'repeat_technical_unit_price',
  'retail_sales',
];

export const KPI_BY_KEY = Object.fromEntries(KPI_REGISTRY.map((e) => [e.key, e]));
export const KPI_BY_FOCUS_KEY = Object.fromEntries(KPI_REGISTRY.map((e) => [e.focusKey, e]));
export const KPI_BY_CHART_KEY = Object.fromEntries(
  KPI_REGISTRY.filter((e) => e.chartKey).map((e) => [e.chartKey, e]),
);

export const SCORECARD_ENTRIES = KPI_REGISTRY.filter((e) => e.scorecard);
export const CHART_ENTRIES = KPI_REGISTRY.filter((e) => e.chartKey);
export const TARGETABLE_ENTRIES = KPI_REGISTRY.filter((e) => e.targetable);

/** Read 実績 for one KPI from an API row (kpis / history / forecast shaped). */
export function readKpiValue(entry, row) {
  if (!entry || !row) return null;
  for (const field of entry.actualFields) {
    const v = row[field];
    if (v != null && !Number.isNaN(Number(v))) return Number(v);
  }
  if (entry.actualDerive) {
    const v = entry.actualDerive(row);
    return v != null && !Number.isNaN(Number(v)) ? Number(v) : null;
  }
  return null;
}

/** Read 予測 for one KPI from an ml_forecast row; falls back to the identity derive. */
export function readForecastValue(entry, row) {
  if (!entry || !row) return null;
  if (entry.forecastField) {
    const v = row[entry.forecastField];
    if (v != null && !Number.isNaN(Number(v))) return Number(v);
  }
  if (entry.actualDerive) {
    const v = entry.actualDerive(row);
    return v != null && !Number.isNaN(Number(v)) ? Number(v) : null;
  }
  return null;
}
